import React, {useCallback, useEffect, useState} from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import moment from "moment";
import dashboardServices from "app/services/dashboard-services";

// El panel de la tarjeta "Con problemas".
//
// Mira la entrega que llego pero llego corta: el camion aparecio, el cliente firmo, y
// aun asi recibio menos de lo que pidio. Una fila por producto al que le falto algo,
// porque es el nivel al que se reclama.
//
// OJO: este panel ya no explica el numero de la tarjeta de arriba. La tarjeta cuenta
// canceladas y atrasos -problemas de cuando llego la entrega- y aca se miran las
// cantidades, que es otra pregunta.
const TONO = "#DC2626";

// Las cantidades vienen como float porque hay unidades de medida que se fraccionan.
// Mostrar "9.0 de 10.0" en una caja de papel es ruido.
const formatearCantidad = (valor) => {
    const numero = Number(valor);
    if (!Number.isFinite(numero)) return "—";

    return Number.isInteger(numero) ? `${numero}` : numero.toFixed(2).replace(/0$/, "");
};

const ProblemBreakdown = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [desde, setDesde] = useState(moment().subtract(29, "days").format("YYYY-MM-DD"));
    const [hasta, setHasta] = useState(moment().format("YYYY-MM-DD"));

    const cargar = useCallback(() => {
        setLoading(true);
        setError(null);

        dashboardServices
            .getProblemBreakdown({desde, hasta})
            .then((res) => setData(res))
            .catch(() => setError("No se pudieron cargar las entregas parciales."))
            .finally(() => setLoading(false));
    }, [desde, hasta]);

    useEffect(() => {
        cargar();
    }, [cargar]);

    const filas = data?.lines || [];
    const totales = data?.totals || {};

    return (
        <Card sx={{mb: 3}}>
            <CardContent>
                <Stack
                    direction={{xs: "column", md: "row"}}
                    justifyContent="space-between"
                    alignItems={{xs: "flex-start", md: "center"}}
                    spacing={1.5}
                    sx={{mb: 2}}
                >
                    <Stack spacing={0.5}>
                        <Typography variant="h4" sx={{mb: 0}}>Con problemas</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Entregas que fueron entregadas de manera parcial.
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1.5}>
                        <TextField
                            id="problem-breakdown-desde"
                            label="Desde"
                            type="date"
                            size="small"
                            value={desde}
                            onChange={(e) => setDesde(e.target.value)}
                            InputLabelProps={{shrink: true}}
                        />
                        <TextField
                            id="problem-breakdown-hasta"
                            label="Hasta"
                            type="date"
                            size="small"
                            value={hasta}
                            onChange={(e) => setHasta(e.target.value)}
                            InputLabelProps={{shrink: true}}
                        />
                    </Stack>
                </Stack>

                {loading && (
                    <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                        <CircularProgress/>
                    </Box>
                )}

                {!loading && error && <Alert severity="error">{error}</Alert>}

                {!loading && !error && filas.length === 0 && (
                    <Alert severity="success">
                        No hubo entregas parciales en este período.
                    </Alert>
                )}

                {!loading && !error && filas.length > 0 && (
                    <>
                        <Stack direction="row" alignItems="baseline" spacing={1} sx={{mb: 2.5}}>
                            <Typography variant="h1" sx={{color: TONO, mb: 0}}>{totales.deliveries}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {totales.deliveries === 1 ? "entrega parcial" : "entregas parciales"}
                                {", "}
                                {totales.lines === 1 ? "1 producto incompleto" : `${totales.lines} productos incompletos`}
                            </Typography>
                        </Stack>

                        {/* Alto fijo con scroll: el rango por defecto son 30 dias, pero al
                            abrirlo a un trimestre la tabla empuja el resto del dashboard
                            varias pantallas para abajo. La cabecera queda pegada porque si
                            no, scrolleando, "0 de 60" deja de decir cual columna es cual. */}
                        <TableContainer sx={{maxHeight: 420}}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fecha</TableCell>
                                        <TableCell>Pedido</TableCell>
                                        <TableCell>Cliente o lugar</TableCell>
                                        <TableCell>Producto</TableCell>
                                        <TableCell align="right">Entregado</TableCell>
                                        <TableCell align="right">Faltó</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filas.map((fila, index) => (
                                        <TableRow key={`${fila.delivery_id}-${fila.product}-${index}`}>
                                            <TableCell>
                                                {fila.ship_date ? moment(fila.ship_date).format("DD/MM") : "—"}
                                            </TableCell>
                                            <TableCell>{fila.order_code || "—"}</TableCell>
                                            <TableCell>{fila.customer || "—"}</TableCell>
                                            <TableCell>{fila.product || "—"}</TableCell>
                                            <TableCell align="right">
                                                {formatearCantidad(fila.delivered_qty)} de {formatearCantidad(fila.qty)}
                                            </TableCell>
                                            <TableCell align="right" sx={{color: TONO, fontWeight: 600}}>
                                                {formatearCantidad(fila.missing_qty)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {totales.lines > filas.length && (
                            <Typography variant="caption" color="text.secondary" sx={{display: "block", mt: 1}}>
                                Se muestran los primeros {filas.length} de {totales.lines} productos incompletos.
                            </Typography>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default ProblemBreakdown;
